import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import blogPosts from '../data/blogPosts';
import Section from '../components/ui/Section';
import NavBar from '../components/NavBar';
import ReactGA from "react-ga4";

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [markdownContent, setMarkdownContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const post = blogPosts.find((p) => p.slug === slug);

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: "/blog-detail",
      title: "Blog Read",
    });
  });

  useEffect(() => {
    if (post) {
      // Update document title
      document.title = post.title + " | TravelMaker Blog";

      // Update meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', post.description);

      if (post.content.startsWith('/')) { // Check if content is a path
        setLoading(true);
        setError(null);
        fetch(post.content)
          .then(response => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.text();
          })
          .then(text => {
            setMarkdownContent(text);
          })
          .catch(err => {
            console.error("Failed to fetch markdown:", err);
            setError("Failed to load blog post content.");
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        // If content is directly embedded (not a path), use it directly
        setMarkdownContent(post.content);
        setLoading(false);
      }
    }
  }, [post]); // Depend on 'post' to re-run when post data is available

  if (!post) {
    return (
      <> {/* Added Fragment */}
        <NavBar /> {/* Added NavBar */}
        <Section className="py-12 pt-24 text-center"> {/* Adjusted padding-top */}
          <h1 className="text-3xl font-bold mb-4">Post Not Found</h1>
          <button
            onClick={() => navigate('/blog')}
            className="bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors duration-300"
          >
            Go to Blog Home
          </button>
        </Section>
      </> // Added Fragment
    );
  }

  if (loading) {
    return (
      <> {/* Added Fragment */}
        <NavBar /> {/* Added NavBar */}
        <Section className="py-12 pt-24 text-center"> {/* Adjusted padding-top */}
          <p>Loading blog post...</p>
        </Section>
      </> // Added Fragment
    );
  }

  if (error) {
    return (
      <> {/* Added Fragment */}
        <NavBar /> {/* Added NavBar */}
        <Section className="py-12 pt-24 text-center"> {/* Adjusted padding-top */}
          <p className="text-red-500">{error}</p>
          <button
            onClick={() => navigate('/blog')}
            className="mt-4 bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors duration-300"
          >
            Go to Blog Home
          </button>
        </Section>
      </> // Added Fragment
    );
  }

  const components = {
    ul: ({ node, ...props }) => <ul className="list-disc pl-5" {...props} />,
    ol: ({ node, ...props }) => <ol className="list-decimal pl-5" {...props} />,
    img: ({ node, ...props }) => {
      let finalSrc = props.src;
      if (props.src && !props.src.startsWith('http') && !props.src.startsWith('/')) {
        // This handles both './image.png' and 'image.png'
        finalSrc = `/blog-content/${post.slug}/${props.src.replace('./', '')}`;
      }
      return <img {...props} src={finalSrc} alt={props.alt} className="my-4 rounded-lg max-w-full h-auto max-h-[600px] object-contain mx-auto block" />;
    },
  };

  return (
    <> {/* Added Fragment */}
      <NavBar /> {/* Added NavBar */}
      <Section className="py-12 pt-24"> {/* Adjusted padding-top */}
        <div className="container mx-auto px-4 max-w-3xl">
          <h1 className="text-5xl font-bold mb-4">{post.title}</h1>
          <p className="text-gray-600 text-sm mb-6">Published on {new Date(post.createdAt).toLocaleDateString()}</p>
          {post.thumbnail && (
            <img src={post.thumbnail} alt={post.title} className="w-full h-96 object-contain rounded-lg mb-8" />
          )}
          <div className="prose-sm lg:prose-lg max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={components}>{markdownContent}</ReactMarkdown>
          </div>
          <div className="mt-10">
            <button
              onClick={() => navigate('/blog')}
              className="bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors duration-300"
            >
              Back to Blog Home
            </button>
          </div>
        </div>
      </Section>
    </> // Added Fragment
  );
};

export default BlogPost;
